
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { checkPaymentStatus } from "@/api/check-payment-status";
import PaymentStatusDialog from "@/components/payment/PaymentStatusDialog";

interface PaymentProcessorProps {
  courseId: string;
  courseName: string;
  price: string;
  onSuccess: () => void;
  webhookUrl?: string; // Optional webhook URL for transaction processed callback
  responseUrl?: string; // Optional response URL for transaction response callback
}

const PaymentProcessor = ({ 
  courseId, 
  courseName, 
  price,
  onSuccess,
  webhookUrl,
  responseUrl
}: PaymentProcessorProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [transactionRef, setTransactionRef] = useState<string | null>(null);
  const [checkCount, setCheckCount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState<{
    isOpen: boolean;
    status: "success" | "failed" | "pending" | null;
    message?: string;
  }>({
    isOpen: false,
    status: null
  });
  const { toast } = useToast();
  const { user } = useAuth();

  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  
  // Effect to auto-check payment status when returning from payment page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    
    // If status is in the URL and we have a payment ID, check payment status
    if ((status === 'pending' || status === 'success') && paymentId && user) {
      handlePaymentCheck();
      
      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('status');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [paymentId, user]);
  
  const handlePayment = async (method: 'card' | 'wallet') => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please log in to make a payment",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const { data: paymentAttempt, error: attemptError } = await supabase
        .from("course_payment_attempts")
        .insert({
          user_id: user.id,
          course_id: courseId,
          amount: numericPrice,
          status: "pending"
        })
        .select()
        .single();
      
      if (attemptError) throw attemptError;
      
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("first_name, last_name, email")
        .eq("id", user.id)
        .single();
      
      if (profileError) throw profileError;
      
      // Ensure we have correct URLs for callbacks
      const supabaseDomain = "https://fmuvniyvftmenrgeywtv.supabase.co";
      
      // Set default callback URLs using Supabase functions
      const effectiveWebhookUrl = webhookUrl || 
        `${supabaseDomain}/functions/v1/paymob-webhook`;
        
      const effectiveResponseUrl = responseUrl || 
        `${supabaseDomain}/functions/v1/paymob-response`;

      // This will be our own internal reference, different from Paymob's order ID
      const internalOrderRef = `${Date.now()}-${courseId.substring(0, 6)}`;
      setTransactionRef(internalOrderRef);
      
      console.log("Starting payment process with Paymob", {
        webhookUrl: effectiveWebhookUrl,
        responseUrl: effectiveResponseUrl,
        internalRef: internalOrderRef
      });
      
      const response = await supabase.functions.invoke("paymob-payment", {
        body: {
          amount: numericPrice,
          courseId: courseId,
          userId: user.id,
          email: profile.email || user.email,
          firstName: profile.first_name || "User",
          lastName: profile.last_name || "",
          callbackUrl: effectiveWebhookUrl, // For transaction processed callback
          transactionRef: internalOrderRef,
          responseUrl: effectiveResponseUrl // For transaction response callback
        }
      });
      
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Payment initialization failed");
      }

      // This is the ORDER ID from Paymob (not the transaction ID yet)
      const paymobOrderId = response.data.paymentId;
      setPaymentId(paymobOrderId);
      
      console.log("Payment initialized with Order ID:", paymobOrderId);
      
      const { error: updateError } = await supabase
        .from("course_payment_attempts")
        .update({ 
          payment_id: paymobOrderId, // This is Paymob's ORDER ID
          transaction_ref: internalOrderRef
        })
        .eq("id", paymentAttempt.id);
      
      if (updateError) {
        console.error("Failed to update payment attempt with payment ID:", updateError);
      }
      
      setPaymentUrl(response.data.paymentUrl);
      
      // Open payment window
      window.open(response.data.paymentUrl, "_blank");
      
      toast({
        title: "Payment initiated",
        description: "Complete your payment in the new window"
      });
      
      // Set a timer to check the payment status after a delay
      setTimeout(() => {
        if (paymentId || internalOrderRef) {
          handlePaymentCheck(true);
        }
      }, 10000); // Check after 10 seconds
      
    } catch (error: any) {
      console.error("Payment error:", error);
      toast({
        title: "Payment failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentCheck = async (silent = false) => {
    if (!user) return;
    
    // Don't run multiple checks simultaneously
    if (isCheckingStatus) return;
    
    setIsCheckingStatus(true);
    setCheckCount(prev => prev + 1);
    
    try {
      let orderIdToCheck = paymentId;
      let transactionRefToCheck = transactionRef;
      
      if (!orderIdToCheck && !transactionRefToCheck) {
        // If we don't have IDs, try to get the most recent pending attempt
        const { data: recentAttempt, error: attemptError } = await supabase
          .from("course_payment_attempts")
          .select("payment_id, transaction_ref")
          .eq("user_id", user.id)
          .eq("course_id", courseId)
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (attemptError) throw attemptError;
        
        if (!recentAttempt || (!recentAttempt.payment_id && !recentAttempt.transaction_ref)) {
          if (!silent) {
            toast({
              title: "No pending payment found",
              description: "Try initiating a new payment",
              variant: "destructive"
            });
          }
          return;
        }
        
        orderIdToCheck = recentAttempt.payment_id;
        transactionRefToCheck = recentAttempt.transaction_ref;
        
        if (orderIdToCheck) setPaymentId(orderIdToCheck);
        if (transactionRefToCheck) setTransactionRef(transactionRefToCheck);
        
        console.log("Found recent payment attempt:", { orderIdToCheck, transactionRefToCheck });
      }
      
      try {
        if (!silent) {
          toast({
            title: "Checking payment status",
            description: "Please wait while we verify your payment..."
          });
        }
        
        // Now we pass orderId (Paymob ORDER ID) and our internal reference
        const response = await checkPaymentStatus({
          paymentId: orderIdToCheck,
          transactionRef: transactionRefToCheck,
          courseId: courseId,
          userId: user.id
        });
        
        console.log("Payment status check response:", response);
        
        if (response.success && response.status === "completed") {
          // Show success dialog with auto-redirect
          setPaymentStatus({
            isOpen: true,
            status: "success",
            message: `Your payment for ${courseName} has been processed successfully! You will be redirected to your course in a moment.`
          });
          
          // Set a timer to redirect and execute onSuccess after showing dialog
          setTimeout(() => {
            setPaymentStatus(prev => ({ ...prev, isOpen: false }));
            onSuccess();
          }, 3000);
          
          // Clear payment state after success
          setPaymentId(null);
          setPaymentUrl("");
          setTransactionRef(null);
        } else if (response.status === "pending") {
          // For pending payments, we might want to check again automatically
          if (!silent || checkCount < 3) {
            toast({
              title: "Payment still processing",
              description: "Your payment is still being processed. We'll check again shortly."
            });
            
            // Schedule another check after a delay if still pending
            if (checkCount < 5) {
              setTimeout(() => {
                handlePaymentCheck(true);
              }, 5000);
            }
          }
        } else {
          if (!silent) {
            setPaymentStatus({
              isOpen: true,
              status: "failed",
              message: response.message || "Try completing your payment or start a new one"
            });
          }
        }
      } catch (error: any) {
        console.error("Payment check error:", error);
        if (!silent) {
          toast({
            title: "Error checking payment",
            description: error.message,
            variant: "destructive"
          });
        }
      }
    } catch (error: any) {
      console.error("Payment check error:", error);
      if (!silent) {
        toast({
          title: "Error checking payment",
          description: error.message,
          variant: "destructive"
        });
      }
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const closePaymentDialog = () => {
    setPaymentStatus(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gray-50 border-b px-6 py-4">
        <h3 className="text-lg font-medium">Purchase Course</h3>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h4 className="font-medium">{courseName}</h4>
            <p className="text-gray-500 text-sm">One-time payment</p>
          </div>
          <div className="text-xl font-bold">{price}</div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Button
              onClick={() => handlePayment('card')}
              disabled={isProcessing || isCheckingStatus}
              className="flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              Pay with Card
            </Button>
            
            {paymentUrl && (
              <div className="flex flex-col space-y-2 mt-4">
                <p className="text-sm text-gray-500 text-center">
                  Already started payment?
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => window.open(paymentUrl, "_blank")}
                    className="text-sm"
                  >
                    Complete Payment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePaymentCheck(false)}
                    disabled={isCheckingStatus}
                    className="text-sm"
                  >
                    {isCheckingStatus ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Check Status"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      
      {/* Payment status dialog */}
      <PaymentStatusDialog
        isOpen={paymentStatus.isOpen}
        onClose={closePaymentDialog}
        status={paymentStatus.status}
        message={paymentStatus.message}
      />
    </Card>
  );
};

export default PaymentProcessor;
